import React, { useState } from 'react';

import { ModalContainer, ModalClose } from 'shared/modals';
import { Categories, Category } from '../categories.types';
import { UploadImage } from './upload-image';
import { ImageLibrary } from './image-library';

type SelectImageModalProps = {
  onClose: () => void;
  category: Category;
  categories: Categories;
  onSave: (updatedCategories: Categories) => void;
};

export const SelectImageModal = ({
  onClose,
  category,
  categories,
  onSave,
}: SelectImageModalProps): JSX.Element | null => {
  const { category: value, image, cardImage, isOutlet } = category;
  const [isUploadImageModalOpen, setIsUploadImageModalOpen] = useState(false);
  const closeModal = isUploadImageModalOpen ? () => setIsUploadImageModalOpen(false) : onClose;
  // For outlet collections, default to cardImage, otherwise use regular image
  const [selectedImage, setSelectedImage] = useState(isOutlet && cardImage ? cardImage : image);

  const saveImage = (): void => {
    const updatedCategories = categories.map((obj) => {
      if (obj.category === value) {
        if (obj.isOutlet) {
          const updated = {
            category: obj.category,
            image: obj.image, // Keep original image field
            collectionId: obj.collectionId,
            isOutlet: obj.isOutlet,
            cardImage: selectedImage,
            cardDisplayName: obj.cardDisplayName,
          };
          return updated;
        }

        // For regular categories, update image field
        const updated = {
          category: obj.category,
          image: selectedImage,
          collectionId: obj.collectionId,
          // Preserve outlet fields if they exist (they shouldn't, but just in case)
          ...(obj.isOutlet !== undefined && { isOutlet: obj.isOutlet }),
          ...(obj.cardImage && { cardImage: obj.cardImage }),
          ...(obj.cardDisplayName && { cardDisplayName: obj.cardDisplayName }),
        };
        return updated;
      }
      return obj;
    });

    onSave(updatedCategories);
    onClose();
  };

  const openUploadImageModal = (): void => {
    setIsUploadImageModalOpen(true);
  };

  return (
    <ModalContainer
      isOpen
      onRequestClose={closeModal}
      width='min(880px, calc(100vw - 32px))'
      alignItems='flex-start'
      padding='40px'
      ariaHideApp={false}
      textAlign='left'
      scrollable
    >
      <ModalClose onClick={closeModal} />

      {isUploadImageModalOpen ? (
        <UploadImage
          category={category}
          setSelectedImage={setSelectedImage}
          setIsUploadImageModalOpen={setIsUploadImageModalOpen}
        />
      ) : (
        <ImageLibrary
          category={category}
          saveImage={saveImage}
          openUploadImageModal={openUploadImageModal}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
        />
      )}
    </ModalContainer>
  );
};
